import * as React from 'react';

import { CreateFooterOptions } from 'src/components/resourceDialog/DialogFooter';
import {
  VirtualGraphDialogState,
  createVgStateDefaults,
  useVirtualGraphDialogState,
} from 'src/containers/ProjectNewResourceDialog/hooks/useVirtualGraphDialogState';
import {
  BaseDialogContextType,
  createBaseDialogContextDefaults,
  useBaseDialogContextValue,
} from 'src/containers/shared/useBaseDialogContext';
import * as copy from 'src/templates/copy';
import { FileCreationMetadata } from 'src/types/fileUpload';

export type FooterOptions = CreateFooterOptions;

export type FileUploadOptions = {
  maxSize?: number;
  acceptedFormats?: string[];
};

export type ProjectResourceDialogContextType =
  BaseDialogContextType<FooterOptions> &
    VirtualGraphDialogState & {
      // File upload state
      selectedFiles: File[];
      setSelectedFiles: (files: File[]) => void;
      fileUploadOptions: FileUploadOptions;
      setFileUploadOptions: (options: FileUploadOptions) => void;
      fileMetadata: Record<number, FileCreationMetadata>;
      setFileMetadata: React.Dispatch<
        React.SetStateAction<Record<number, FileCreationMetadata>>
      >;

      resetDialogState: () => void;
    };

export const ProjectResourceDialogContext =
  React.createContext<ProjectResourceDialogContextType>({
    ...createBaseDialogContextDefaults<FooterOptions>(),
    selectedFiles: [],
    setSelectedFiles: () => {},
    fileUploadOptions: {},
    setFileUploadOptions: () => {},
    fileMetadata: {},
    setFileMetadata: () => {},
    ...createVgStateDefaults(),
    resetDialogState: () => {},
  });

export const useProjectResourceDialog = () => {
  return React.useContext(ProjectResourceDialogContext);
};

export const useProjectResourceDialogContextValue = () => {
  const {
    fullSize,
    setFullSize,
    dialogTitle,
    setDialogTitle,
    headerBackButton,
    setHeaderBackButton,
    isLoading,
    setIsLoading,
    footerOptions,
    setFooterOptions,
  } = useBaseDialogContextValue<FooterOptions>(
    copy.components.projectResourceDialog.resourceTypePanel.titleAdd
  );

  const [selectedFiles, setSelectedFiles] = React.useState<File[]>([]);
  const [fileUploadOptions, setFileUploadOptions] =
    React.useState<FileUploadOptions>({});
  const [fileMetadata, setFileMetadata] = React.useState<
    Record<number, FileCreationMetadata>
  >({});

  const vgState = useVirtualGraphDialogState();

  const { resetVgState } = vgState;

  const resetDialogState = React.useCallback(() => {
    // Reset file state
    setSelectedFiles([]);
    setFileMetadata({});
    setFileUploadOptions({});
    // Reset VG state
    resetVgState();
    // Reset dialog chrome
    setFullSize(false);
    setDialogTitle(
      copy.components.projectResourceDialog.resourceTypePanel.titleAdd
    );
    setHeaderBackButton(null);
    setFooterOptions({});
    setIsLoading(false);
  }, [
    resetVgState,
    setFullSize,
    setDialogTitle,
    setHeaderBackButton,
    setFooterOptions,
    setIsLoading,
  ]);

  return React.useMemo(
    () => ({
      fullSize,
      setFullSize,
      dialogTitle,
      setDialogTitle,
      headerBackButton,
      setHeaderBackButton,
      isLoading,
      setIsLoading,
      footerOptions,
      setFooterOptions,
      selectedFiles,
      setSelectedFiles,
      fileUploadOptions,
      setFileUploadOptions,
      fileMetadata,
      setFileMetadata,
      ...vgState,
      resetDialogState,
    }),
    [
      fullSize,
      setFullSize,
      dialogTitle,
      setDialogTitle,
      headerBackButton,
      setHeaderBackButton,
      isLoading,
      setIsLoading,
      footerOptions,
      setFooterOptions,
      selectedFiles,
      fileUploadOptions,
      fileMetadata,
      vgState,
      resetDialogState,
    ]
  );
};
